import { defineStore } from "pinia";
import { ref } from "vue";
import companyService from "@/services/super-admin/companyService";
import { toast } from "@/utils/toast";
import { setActiveCompanyId } from "@/services/_headers.util";

export const useCompanyStore = defineStore("company", () => {
  const companies = ref([]);
  const company = ref(null);
  const loading = ref(false);
  const error = ref(null);

  const activeCompanyId = ref(
    Number(localStorage.getItem("activeCompanyId")) || null
  );
  function setActiveCompany(id) {
    activeCompanyId.value = id ? Number(id) : null;
    if (id) localStorage.setItem("activeCompanyId", String(id));
    else localStorage.removeItem("activeCompanyId");
  }
  // optional default after fetch
  // function ensureActiveDefault() {
  //   if (!activeCompanyId.value && companies.value.length) {
  //     setActiveCompany(companies.value[0].id);
  //   }
  // }

  async function fetchCompanies(params = {}, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      const res = await companyService.getAll({
        per_page: 1000,
        ...params,
      }, companyId);
      companies.value = res.data?.data || res.data || [];
      // ensureActiveDefault();
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to fetch companies";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function fetchCompany(id, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      const res = await companyService.get(id, companyId);
      company.value = res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to fetch company";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function fetchCurrentCompany() {
    loading.value = true;
    error.value = null;
    try {
      const res = await companyService.getCurrent();
      company.value = res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to fetch company";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function createCompany(data, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      const res = await companyService.create(data, companyId);
      const payload = res?.data;
      const created = payload?.company || payload;
      const newId = created?.id;

      if (!newId) {
        const status = res?.status;
        const contentType = res?.headers?.["content-type"] || res?.headers?.["Content-Type"] || "";
        const preview =
          typeof payload === "string"
            ? payload.slice(0, 120)
            : JSON.stringify(payload)?.slice(0, 180);

        error.value =
          `Company create returned an unexpected response (HTTP ${status || "?"}, ${contentType || "unknown type"}). ` +
          `Nothing was saved. Response: ${preview || "(empty)"}`;
        toast(error.value, "error");
        throw new Error(error.value);
      }

      toast("Company created successfully");
      setActiveCompanyId(newId);
      activeCompanyId.value = Number(newId);

      await fetchCompanies({}, companyId);
      return res.data;
    } catch (err) {
      const validation =
        err.response?.data?.errors &&
        Object.values(err.response.data.errors).flat().filter(Boolean)[0];
      error.value =
        validation ||
        err.response?.data?.message ||
        err.message ||
        "Failed to create company";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateCompany(id, data, silent = false, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      const res = await companyService.update(id, data, companyId);

      // 🔹 Only show toast when NOT silent
      if (!silent) {
        toast("Company updated successfully");
      }

      await fetchCompanies({}, companyId);
      return res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to update company";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function changeCompanyPlan(id, data, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      const res = await companyService.changePlan(id, data, companyId);
      toast("Company plan updated successfully");
      return res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to update company plan";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchCompanyModuleOverrides(id, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      const res = await companyService.getModuleOverrides(id, companyId);
      return res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to fetch company module overrides";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateCompanyModuleOverrides(id, data, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      const res = await companyService.updateModuleOverrides(id, data, companyId);
      toast("Company module overrides updated successfully");
      return res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to update company module overrides";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteCompany(id, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      await companyService.delete(id, companyId);
      toast("Company deleted successfully");
      await fetchCompanies({}, companyId);
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to delete company";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      await companyService.bulkDelete(ids, companyId);
      toast("Companies deleted successfully");
      await fetchCompanies({}, companyId);
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to bulk delete";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    companies,
    company,
    loading,
    error,
    activeCompanyId,
    setActiveCompany,
    // ensureActiveDefault,
    fetchCompanies,
    fetchCompany,
    fetchCurrentCompany,
    createCompany,
    updateCompany,
    changeCompanyPlan,
    fetchCompanyModuleOverrides,
    updateCompanyModuleOverrides,
    deleteCompany,
    bulkDelete,
  };
});
